/**
 * SwarmMoon — the NightSwarm mark: a crescent moon assembled from a swarm
 * of dots, with a few stragglers still converging on the open side.
 *
 * Exact port of `crescentDots(7)` from design/project/lib/ns-kit.jsx.
 * Deterministic (seeded mulberry32) so the mark renders identically every
 * time — and matches the brand assets emitted by scripts/generate-logo.mjs,
 * which shares this geometry. Change one, change both.
 */
import type { CSSProperties } from "react";

/** Seeded PRNG so the swarm mark is deterministic. */
export function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return function () {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export interface SwarmDot {
  x: number;
  y: number;
  r: number;
  accent: boolean;
  stray?: boolean;
}

/**
 * Generate dots that form a crescent moon (a disc minus an offset disc),
 * plus a few stray "converging" swarm dots. 100×100 viewBox coordinates.
 */
export function crescentDots(seed = 7): SwarmDot[] {
  const rnd = mulberry32(seed);
  const moon = { x: 48, y: 50, r: 43 };
  const carve = { x: 66, y: 44, r: 42 };
  const dots: SwarmDot[] = [];
  const step = 4.5;
  for (let gx = 4; gx <= 96; gx += step) {
    for (let gy = 4; gy <= 96; gy += step) {
      const x = gx + (rnd() - 0.5) * step * 0.85;
      const y = gy + (rnd() - 0.5) * step * 0.85;
      const dMoon = Math.hypot(x - moon.x, y - moon.y);
      const dCarve = Math.hypot(x - carve.x, y - carve.y);
      if (dMoon > moon.r) continue;
      // soft inner edge: thin fade of dots just inside the carve
      if (dCarve < carve.r) {
        if (dCarve < carve.r - 2.5) continue;
        if (rnd() > 0.5) continue;
      }
      // gentle density fade near the outer rim
      const rimT = dMoon / moon.r;
      if (rimT > 0.92 && rnd() > 0.78) continue;
      const r = 1.3 + rnd() * 1.0;
      // accent dots gathered in the dense belly (left-center)
      const accent = x < 36 && y > 40 && y < 64 && rnd() > 0.82;
      dots.push({ x, y, r, accent });
    }
  }
  // stray swarm dots converging into the open side of the crescent
  for (let i = 0; i < 7; i++) {
    const ang = -0.4 + rnd() * 1.4;
    const dist = 40 + rnd() * 12;
    dots.push({
      x: carve.x + Math.cos(ang) * dist * 0.62,
      y: carve.y + Math.sin(ang) * dist * 0.92,
      r: 0.8 + rnd() * 0.85,
      accent: rnd() > 0.6,
      stray: true,
    });
  }
  return dots;
}

/** Computed once at module load — the canonical mark. */
export const SWARM_DOTS: readonly SwarmDot[] = crescentDots(7);

export interface SwarmMoonProps {
  size?: number;
  /** Dots breathe gently (gated behind prefers-reduced-motion). */
  animate?: boolean;
  /** Render every dot in currentColor (menubar/compact contexts). */
  mono?: boolean;
}

export function SwarmMoon({
  size = 28,
  animate = false,
  mono = false,
}: SwarmMoonProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 100 100"
      role="img"
      aria-label="NightSwarm"
      style={{ display: "block", overflow: "visible" }}
    >
      {SWARM_DOTS.map((d, i) => {
        const fill = mono
          ? "currentColor"
          : d.accent
            ? "var(--color-accent)"
            : "var(--color-fg-1)";
        const op = d.stray ? 0.5 : d.accent ? 1 : 0.92;
        const animStyle: CSSProperties | undefined = animate
          ? ({
              "--ns-breath-dur": `${2.6 + (i % 7) * 0.18}s`,
              "--ns-breath-delay": `${(i % 11) * 0.13}s`,
              transformOrigin: `${d.x}px ${d.y}px`,
            } as CSSProperties)
          : undefined;
        return (
          <circle
            key={i}
            cx={d.x}
            cy={d.y}
            r={d.r}
            fill={fill}
            opacity={op}
            className={animate ? "ns-breath" : undefined}
            style={animStyle}
          />
        );
      })}
    </svg>
  );
}

/** Swarm-moon + "nightswarm" wordmark lockup. */
export function NSWordmark({
  size = 18,
  mark = true,
  animate = false,
}: {
  size?: number;
  mark?: boolean;
  animate?: boolean;
}) {
  return (
    <span
      style={{ display: "inline-flex", alignItems: "center", gap: size * 0.42 }}
    >
      {mark && <SwarmMoon size={size * 1.5} animate={animate} />}
      <span
        style={{
          fontFamily: "var(--font-mono)",
          fontWeight: 600,
          fontSize: size,
          letterSpacing: "-0.02em",
          color: "var(--color-fg-1)",
        }}
      >
        nightswarm
      </span>
    </span>
  );
}
